import React, { useState } from 'react';
import { ArrowLeft, Loader2, PartyPopper, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { Paso5State } from '../../../hooks/api/baja';

interface Props {
  initial: Paso5State | null;
  sending: boolean;
  sent: boolean;
  onBack: () => void;
  onSend: (state: Paso5State) => void;
}

export const Paso5Reunion: React.FC<Props> = ({ initial, sending, sent, onBack, onSend }) => {
  const [reunionCoordinada, setReunionCoordinada] = useState(initial?.reunionCoordinada ?? false);
  const [notas, setNotas] = useState(initial?.notas ?? '');
  const [intentoEnviar, setIntentoEnviar] = useState(false);

  const handleEnviar = () => {
    if (!reunionCoordinada) { setIntentoEnviar(true); return; }
    onSend({ reunionCoordinada, notas: notas || undefined });
  };

  if (sent) {
    return (
      <div className="mx-auto w-full max-w-2xl text-center space-y-4 py-10">
        <PartyPopper className="h-10 w-10 text-primary mx-auto" />
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">¡Listo! Registramos tu proceso de salida</h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          El equipo de Psi Mammoliti va a revisar lo declarado en los pasos anteriores. Gracias por completar el
          proceso con seriedad.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Reunión de salida</h1>
        <p className="text-sm text-muted-foreground">
          La reunión de cierre con el equipo es un paso necesario del proceso, no una formalidad opcional: es donde
          se termina de revisar la transición de tus pacientes y se cierra tu vínculo con Psi Mammoliti.
        </p>
      </header>

      <section className="rounded-2xl border border-primary/25 bg-primary/5 p-6 space-y-5">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary shrink-0">
            <Handshake className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-gray-900">Coordinación con el equipo</h2>
        </div>

        <label className="flex items-start gap-3 text-sm text-gray-800">
          <input
            type="checkbox"
            className="accent-primary mt-0.5 h-4 w-4 shrink-0"
            checked={reunionCoordinada}
            onChange={(e) => { setReunionCoordinada(e.target.checked); setIntentoEnviar(false); }}
          />
          <span>
            Ya coordiné —o me comprometo a coordinar antes de mi fecha de salida— una reunión de cierre directo con{' '}
            <span className="font-semibold text-gray-900">administracion@psimammoliti.com</span>. Entiendo que este paso es
            necesario para cerrar el proceso de forma responsable con mis pacientes y con el equipo.
          </span>
        </label>
        {intentoEnviar && !reunionCoordinada && (
          <p className="text-xs text-coral -mt-2">Tenés que confirmar este punto para poder finalizar el proceso.</p>
        )}

        <div className="space-y-2 pt-1 border-t border-primary/15">
          <label className="text-sm font-medium text-gray-700 block pt-4">
            Notas para el equipo <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <p className="text-xs text-muted-foreground">
            Si ya tenés fecha y horario coordinados, contanoslo acá. Si todavía no, contanos cómo pensás coordinarlo.
          </p>
          <Textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={3}
            className="bg-white"
          />
        </div>
      </section>

      <div className="flex justify-between pt-4 border-t border-border">
        <Button type="button" variant="ghost" onClick={onBack} disabled={sending}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>
        <Button type="button" onClick={handleEnviar} disabled={sending} className="sm:min-w-[200px]">
          {sending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          Enviar y finalizar
        </Button>
      </div>
    </div>
  );
};

export default Paso5Reunion;
